import React, { useEffect, useRef, useState } from "react";
import { Button, Card, Popover, SkeletonLoader, Stack, Text, useTheme } from "@bookingcom/bui-react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import useGoToFlightDetails from "../../../../branded-fares/hooks/useGoToFlightDetails.js";
import Frame from "../../Frame/index.js";
import FareFeatures from "../../../../branded-fares/components/FareFeatures/index.js";
import FareFeaturesIcons from "../../../../branded-fares/components/FareFeatureIcons/index.js";
import FareCardPrice from "./FareCardPrice.js";
import styles from "./FareCard.desktop.module.css";
import { areExcludedFeaturesRequiredByAirline, areSellableFeaturesRequiredByAirline } from "@flights/ancillaries-universal";
import { formatFareName } from "@flights/branded-fares";
import flights_apex_branded_fares_loading_sr from "../../../../utils/experiments/apex/flights_apex_branded_fares_loading_sr.js";
import FareCardCampaignBadges from "./FareCardCampaignBadges.js";
import flights_apex_tabular_branded_fares_sr from "../../../../utils/experiments/apex/flights_apex_tabular_branded_fares_sr.js";
import { trackSplitBoundsBrandedFareSelection } from "../../../../utils/experiments/funnel/flights_split_bounds_us.js";
import useGetSearchResultsBound from "../../../../hooks/useGetSearchResultsBound.js";
import { useSelector } from "react-redux";
import { getSearch } from "../../../../store/search/selectors.js";
const FareCardDesktop = ({ baseOffer, fareOffer, loadingStatus, index, numberOfOffers, variant }) => {
    const i18n = useI18n();
    const theme = useTheme();
    const popoverRef = useRef(document.body);
    const goToFlightDetails = useGoToFlightDetails();
    const sellableFeaturesRequiredByAirline = areSellableFeaturesRequiredByAirline(baseOffer.segments);
    const excludedFeaturesRequiredByAirline = areExcludedFeaturesRequiredByAirline(baseOffer.segments);
    const [popoverShown, setPopoverShown] = useState(false);
    const isTabularBrandedFaresExperimentEnabled = !!flights_apex_tabular_branded_fares_sr.trackWithDefaultStage();
    const bound = useGetSearchResultsBound();
    const { brandedFareInfo, includedProducts } = fareOffer;
    const search = useSelector(getSearch);
    const isMultiTraveller = (search.adults || 0) + (search.children || []).length > 1;
    useEffect(() => {
        if (isMultiTraveller) {
            flights_apex_tabular_branded_fares_sr.stages.multitravellers();
        }
    }, [isMultiTraveller]);
    useEffect(() => {
        brandedFareInfo && flights_apex_tabular_branded_fares_sr.trackStages();
    }, [brandedFareInfo]);
    const renderedFeatures = (React.createElement(FareFeatures, { brandedFareInfo: brandedFareInfo, sellableFeaturesRequiredByAirline: sellableFeaturesRequiredByAirline, excludedFeaturesRequiredByAirline: excludedFeaturesRequiredByAirline, showHeader: true, isPreCheckAndPay: true, areShown: popoverShown, includedProducts: includedProducts }));
    return (React.createElement(Popover, { triggerType: "hover", containerRef: popoverRef, onOpen: () => {
            setPopoverShown(true);
            flights_apex_tabular_branded_fares_sr.goals.user_hover_bf_feature_icons();
            flights_apex_tabular_branded_fares_sr.stages.users_open_tooltip();
        }, onClose: () => {
            setPopoverShown(false);
        } },
        React.createElement(Popover.Trigger, { className: styles.popoverTrigger }, (attributes) => (React.createElement(Card, { className: styles.card },
            React.createElement(Frame, { attributes: {
                    ...attributes,
                    tabIndex: 0
                }, justifyContent: "space-between" },
                React.createElement(Text, { variant: "strong_2", attributes: { "data-fare-card-row": "title" } }, loadingStatus === "loading" ? (React.createElement(Stack, { gap: 6, direction: "row" },
                    React.createElement(Stack.Item, { grow: true },
                        React.createElement(SkeletonLoader, { variant: "title" })),
                    React.createElement(SkeletonLoader, { width: theme.units.spacing_12x }))) : (formatFareName(brandedFareInfo?.fareName))),
                React.createElement(Frame, { mt: 2, "data-fare-card-row": "icons" },
                    React.createElement(FareFeaturesIcons, { brandedFareInfo: brandedFareInfo, isCompact: true, isPreCheckAndPay: true })),
                React.createElement(Frame, { mt: 2 },
                    React.createElement(FareCardPrice, { fareOffer: fareOffer, baseOffer: baseOffer, isBold: true, showPricePerPerson: true, variant: variant }))),
            React.createElement(Frame, { mt: 1, className: styles.badgesWrapper }, loadingStatus !== "loading" && React.createElement(FareCardCampaignBadges, { fareOffer: fareOffer, baseOffer: baseOffer })),
            React.createElement(Frame, { className: styles.buttonWrapper },
                React.createElement(Button, { wide: true, onClick: () => {
                        flights_apex_branded_fares_loading_sr.trackFareCardClick();
                        trackSplitBoundsBrandedFareSelection({ bound, index });
                        flights_apex_tabular_branded_fares_sr.goals.user_click_fare_child_offer();
                        if (!isTabularBrandedFaresExperimentEnabled && index !== undefined && index > 0) {
                            flights_apex_tabular_branded_fares_sr.goals.user_click_fare_child_offer_upsell();
                        }
                        goToFlightDetails(fareOffer.token, baseOffer, index, fareOffer.brandedFareInfo?.fareName, fareOffer.travellerPrices?.[0]?.travellerPriceBreakdown?.total);
                    }, variant: "secondary", attributes: {
                        ...(index !== undefined && {
                            "aria-label": `${i18n.trans(t("a11y_flights_sr_branded_fares_view_cta", {
                                variables: { fare_name: formatFareName(fareOffer.brandedFareInfo?.fareName) }
                            }))}, ${index + 1} of ${numberOfOffers}`
                        })
                    } }, i18n.trans(t("flights_apex_bf_sr_bf_card_cta"))))))),
        renderedFeatures ? (React.createElement(Popover.Content, { attributes: {
                style: {
                    width: 240,
                    whiteSpace: "pre-wrap",
                    overflow: "hidden"
                }
            } }, renderedFeatures)) : null));
};
export default FareCardDesktop;
